import React, { FC } from 'react';
import { Box, Card, CardContent, CardHeader, Link, styled } from '@mui/material';
import { Grid } from '@mui/system';
import { isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { useFormat } from 'common/hooks';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { SubscriptionExpiredBox } from 'dashboard409a/components/SubscriptionExpiredBox';
import { TrendsDetail } from 'dashboard409a/components/TrendsDetail';
import { ITrendsDetailProps } from 'dashboard409a/components/TrendsDetail/types';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { RootStateType } from 'dashboard409a/states/store';
import { ValuationTrendCardsProps } from './types';
import { EnterpriseValueChart } from '../EnterpriseValueChart';
import { ReportsCardHeader } from '../ReportsCardHeader';

const PREFIX = 'ValuationTrendCards';

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  boxTrends: `${PREFIX}-boxTrends`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.root}`]: {
    marginBottom: '2%',
    boxShadow: 'none',
  },

  [`& .${classes.link}`]: {
    display: 'inline',
    cursor: 'pointer',
  },

  [`& .${classes.boxTrends}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '2%',
  },
}));

// TODO(dashboard-409-revamp): the Figma redesign has no Trends Details table.
// Hidden rather than deleted until we confirm it isn't coming back — flip to
// true to restore it, or delete the table, the link, and this flag.
const SHOW_TRENDS_DETAILS_UI = false;

const ValuationTrendCards: FC<ValuationTrendCardsProps> = ({ showTrendsDetails, displayTable }) => {
  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const { loading, allValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(state => state.valuation);
  const [format, formatDispatch] = useFormat({ units: REGULAR_UNIT });

  const subscriptionIsActive = currentCompany?.is_active;
  const displayExpired = !isUndefined(subscriptionIsActive) && subscriptionIsActive === false;

  const trendsDetailProps: ITrendsDetailProps = {
    data: allValuationScalarOpinion,
    isLoading: loading,
    format,
    formatDispatch,
  };

  return (
    <StyledGrid size={{ xs: 12, lg: 6 }}>
      <Card className={classes.root}>
        <CardHeader title={<ReportsCardHeader title="Enterprise Value" />} disableTypography />
        <CardContent>
          {displayExpired ? (
            <SubscriptionExpiredBox />
          ) : (
            <EnterpriseValueChart data={allValuationScalarOpinion} isLoading={loading} />
          )}
          {SHOW_TRENDS_DETAILS_UI && (
            <Box className={classes.boxTrends}>
              <Link
                id="show-valuation-methods-link"
                onClick={() => displayTable('ValuationTrends')}
                className={classes.link}
                variant="body2">
                {`${showTrendsDetails ? 'Hide' : 'Show'} Trends Details`}
              </Link>
            </Box>
          )}
        </CardContent>
      </Card>
      {SHOW_TRENDS_DETAILS_UI && showTrendsDetails ? (
        <Card>{displayExpired ? <SubscriptionExpiredBox /> : <TrendsDetail {...trendsDetailProps} />}</Card>
      ) : null}
    </StyledGrid>
  );
};

export default ValuationTrendCards;
